// PostHog Analytics and Error Tracking
declare global {
  interface Window {
    posthog?: {
      capture: (eventName: string, properties?: Record<string, any>) => void;
      identify: (distinctId: string, properties?: Record<string, any>) => void;
      register: (properties: Record<string, any>) => void;
      captureException: (error: Error, properties?: Record<string, any>) => void;
    };
  }
}

// EVENT CAPTURE FUNCTIONS

// Generic function to capture events with custom event name and properties
export const capturePosthogEvent = (eventName: string, properties: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(eventName, properties);
  }
};

// ERROR TRACKING FUNCTIONS

// Simple function to track errors
export const trackError = (errorType: string, error: Error | string, context?: Record<string, any>) => {
  if (typeof window !== 'undefined' && window.posthog) {
    const message = error instanceof Error ? error.message : String(error);
    
    // Create an Error object for PostHog's error tracking
    const errorObj = error instanceof Error ? error : new Error(message);
    
    console.log('Tracking error:', errorType, message, context);
    
    // Try using PostHog's captureException method if available
    if (window.posthog.captureException) {
      console.log('Using captureException method');
      window.posthog.captureException(errorObj, {
        errorType,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        ...context
      });
    } else {
      console.log('Using manual $exception capture');
      // Fallback to manual $exception capture
      window.posthog.capture('$exception', {
        $exception_type: errorType,
        $exception_message: message,
        $exception_stack: errorObj.stack,
        $exception_url: window.location.href,
        $exception_timestamp: new Date().toISOString(),
        $exception_fingerprint: `${errorType}-${message}`,
        $exception_level: 'error',
        ...context
      });
    }
  } else {
    console.log('PostHog not available for error tracking');
  }
};

// Track API errors
export const trackApiError = (error: Error | string, method: string, url: string, status?: number, requestBody?: string) => {
  trackError('api_error', error, {
    'data-mg-api-method': method,
    'data-mg-api-url': url,
    'data-mg-api-status': status,
    'data-mg-api-request': requestBody
  });
};

// Track UI errors
export const trackUIError = (error: Error | string, component?: string) => {
  trackError('ui_error', error, {
    'data-mg-error-component': component || 'ui'
  });
};

// Initialize global error handlers
export const initializeErrorTracking = () => {
  if (typeof window === 'undefined') return;

  // Console errors
  // const originalConsoleError = console.error;
  // console.error = (...args: any[]) => {
  //   originalConsoleError.apply(console, args);
  //   const message = args.map(arg => String(arg)).join(' ');
  //   trackError('console_error', message);
  // };

  // Uncaught errors
  window.addEventListener('error', (event) => {
    trackError('javascript_error', event.error || event.message, {
      'data-mg-error-filename': event.filename,
      'data-mg-error-lineno': event.lineno
    });
  });

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    trackError('unhandled_promise_rejection', event.reason);
  });
};

// INPUT CAPTURE FUNCTIONS

// Function to set up input value capture for web environment
export const setupInputValueCapture = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const isInteractiveTestId = (testId: string) => {
    const lowerTestId = testId.toLowerCase();
    return (lowerTestId.includes('button') || lowerTestId.includes('option'));
  };

  const isValidTextContent = (text: string) => {
    const trimmed = text.trim();
    return trimmed !== '' && trimmed !== 'N/A' && trimmed !== '0.00';
  };

  const isInputElement = (element: HTMLElement): element is HTMLInputElement | HTMLTextAreaElement => {
    return element.tagName === 'INPUT' || element.tagName === 'TEXTAREA';
  };

  // Capture existing text elements with testID on page load
  // Use longer delay to allow for data loading
  setTimeout(() => {
    const elements = document.querySelectorAll<HTMLElement>('[data-testid]');

    elements.forEach((element) => {
      const testId = element.getAttribute('data-testid') || '';
      if (!testId || isInteractiveTestId(testId)) return;

      const text = isInputElement(element) ? element.value : element.textContent;
      if (text && isValidTextContent(text) && !isInteractiveTestId(testId)) {
        
        // capturePosthogEvent(`text_${testId}`, {
        //   'data-mg-field-name': testId,
        //   'data-mg-field-value': text,
        // });
      }
    });
  }, 5000); // Increased delay to 5 seconds

  // Capture blur events on inputs (React Native TextInput simulation)
  document.addEventListener('blur', (event) => {
    const target = event.target as HTMLInputElement;
    if (target && target.tagName === 'INPUT' && target.getAttribute('data-testid') && !target.name && !target.id) {

      const fieldName = target.getAttribute('data-testid')!;
      const fieldType = target.type || 'text';
      const fieldValue = target.value;

      // if (!['password', 'hidden'].includes(fieldType)) {
      //   capturePosthogEvent(`input_${fieldName}`, {
      //     'data-mg-field-name': fieldName,
      //     'data-mg-field-type': fieldType,
      //     'data-mg-field-value': fieldValue,
      //   });
      // }
    }
  }, true);

  // Handle React Native TouchableOpacity dropdown/option selections and button clicks
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;

    const processElement = (el: HTMLElement) => {
      const testId = el.getAttribute('data-testid')?.trim();
      if (!testId) return;

      const text = el.textContent?.trim() || el.innerText?.trim() || '';

      // Check if this is a clickable element (button, option, or TouchableOpacity)
      const isClickable = el.tagName === 'BUTTON' || el.getAttribute('role') === 'button' || 
                         /button/i.test(testId) || /option/i.test(testId) ||
                         el.onclick !== null || el.getAttribute('onclick') !== null ||
                         el.style.cursor === 'pointer';

      // if (isClickable) {
      //   capturePosthogEvent(`button_clicked_${testId}`, {
      //     'data-mg-button-text': text || testId,
      //     'data-mg-button-testid': testId,
      //     'data-mg-button-type': 'TouchableOpacity'
      //   });
      // } else if (text) {
      //   capturePosthogEvent(`input_${testId}`, {
      //     'data-mg-field-name': testId,
      //     'data-mg-field-type': 'dropdown',
      //     'data-mg-field-value': text,
      //   });
      // }
    };

    // Find the element with testID by traversing up the DOM tree
    let elementWithTestId: HTMLElement | null = target;
    while (elementWithTestId && !elementWithTestId.getAttribute('data-testid')) {
      elementWithTestId = elementWithTestId.parentElement;
    }
    
    if (elementWithTestId && elementWithTestId.getAttribute('data-testid')) {
      processElement(elementWithTestId);
    }
  }, true);

}
